import React, { useEffect, useState } from "react";
import { Tabs as MUITabs, Tab } from "@mui/material";
import { TabType } from "../../../services/helpers/types";
import useIsLoading from "../../../services/hooks/useIsLoading";
import TabsSkeleton from "./TabsSkeleton";
import useIsEditMode from "../../../services/hooks/useEditMode";
import DraggableTabsList from "../../atoms/DraggableTabsList/DraggableTabsList";
import classNames from "classnames";
import Button from "../../atoms/Button/Button";
import { Add } from "@mui/icons-material";
import { createNewTab, CreateTabBody } from "../../../services/api/tabs.api";
import { useDispatch } from "react-redux";
import { getMaxPosition } from "../../../services/helpers/commonHelpers";
import { useTranslation } from "react-i18next";

export interface SelectedTabType {
  index: number;
  tabID: number;
}

interface TabsProps {
  items: TabType[];
  currentTab?: number;
  sidebarItemID?: number;
  onSelectTab: (
    tabID: number,
    index: number,
    shouldChangePage: boolean,
  ) => void;
  onCreateTab?: (tabID: number, index: number) => void;
}

const Tabs = (props: TabsProps) => {
  const { items, currentTab, sidebarItemID, onSelectTab, onCreateTab } = props;

  const [editTabs, setEditTabs] = useState<TabType[]>(items);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<SelectedTabType>({
    index: 0,
    tabID: 0,
  });

  const { isTabsLoading } = useIsLoading();
  const isInEditMode = useIsEditMode();

  useEffect(() => {
    if (isInEditMode) {
      setEditTabs(items);
    }
  }, [isInEditMode, items]);

  useEffect(() => {
    if (items.length !== 0) {
      setSelectedTab({ tabID: items[0].tabID, index: 0 });
    }
  }, [items]);

  const handleSelectTab = (tab: TabType, index: number) => {
    if (tab.tabID !== selectedTab.index) {
      setSelectedTab({ index: index, tabID: tab.tabID });
      onSelectTab(tab.tabID, index, true);
    }
  };

  const handlePositionChange = (
    index: number,
    tabID: number,
    newTabs: TabType[],
  ) => {
    setEditTabs(newTabs);
    setSelectedTab({ index: index, tabID: selectedTab.tabID });
    onSelectTab(tabID, index, false);
  };

  const handleCreateNewTab = () => {
    const maxPosition = getMaxPosition(editTabs);

    if (sidebarItemID !== undefined) {
      const body: CreateTabBody = {
        sidebarItemID,
        position: maxPosition + 1,
        name: t("newTabItem"),
      };
      createNewTab(dispatch, body).then((response) => {
        const newTabID = response[0].tabID;
        if (newTabID !== undefined && onCreateTab !== undefined) {
          setEditTabs([
            ...editTabs,
            {
              tabID: newTabID,
              name: t("newTabItem"),
              position: maxPosition + 1,
            },
          ]);
          if (editTabs.length === 0) {
            onCreateTab(newTabID, maxPosition + 1);
          }
        }
      });
    }
  };

  const handleTabUpdate = (
    tabID: number,
    name: string,
    value: string,
    position: number,
  ) => {
    const newTabs = editTabs.map((tab) => {
      if (tab.tabID === tabID) {
        return {
          tabID: tabID,
          name: name,
          value: value,
          position: position,
        };
      }
      return tab;
    });
    setEditTabs(newTabs);
  };

  const handleDeleteTab = (itemID: number) => {
    setEditTabs(editTabs.filter((tab) => tab.tabID !== itemID));
  };

  if (isTabsLoading) {
    return <TabsSkeleton />;
  }

  const className = classNames("dui-tabs", {
    "dui-tabs-edit": isInEditMode,
  });
  return (
    <div className={className}>
      {isInEditMode ? (
        <div className="dui-tabs-edit-wrapper">
          <div className="dui-tabs-edit-inner">
            <DraggableTabsList
              currentTab={selectedTab}
              onTabChange={(itemID, index, forceRefresh) => {
                if (forceRefresh) {
                  onCreateTab?.(itemID, index);
                }
                onSelectTab(itemID, index, true);
                setSelectedTab({ index: index, tabID: itemID });
              }}
              tabs={editTabs}
              onPositionChange={handlePositionChange}
              onDeleteTab={handleDeleteTab}
              onTabUpdate={handleTabUpdate}
            />
          </div>
          <div className="dui-tabs-edit-add-wrapper">
            <Button
              icon={<Add />}
              type="primary"
              corners="circle"
              className="dui-tabs-edit-add-button"
              onClick={handleCreateNewTab}
            />
          </div>
          {editTabs.length === 0 && (
            <span className="dui-tabs-edit-add-text">
              {"<--- Tu vytvorte novú záložku"}
            </span>
          )}
        </div>
      ) : (
        <MUITabs value={currentTab ?? selectedTab} className="dui-tabs-inner">
          {items.map((item, index) => (
            <Tab
              key={index}
              label={item.name}
              value={index}
              className="dui-tab"
              onClick={() => handleSelectTab(item, index)}
            />
          ))}
        </MUITabs>
      )}
    </div>
  );
};

export default Tabs;
